import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { useToast } from "../../hooks/useToast";

import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import {
  Grid,
  Paper,
  Tab,
  Tabs,
  Avatar,
  Box,
  IconButton,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { PenLine, User } from "lucide-react";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import WhatsappSelect from "../WhatsappSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { resolveUserAvatar } from "../../utils/avatar";
import useWhatsApps from "../../hooks/useWhatsApps";
import { Can } from "../Can";
import TabPanel from "../TabPanel";
import StandardModal from "../ui/StandardModal";
import AvatarModal from "./AvatarModal";
import GroupAccessField from "./GroupAccessField";
import { getBackendUrl } from "../../config";
import usePlans from "../../hooks/usePlans";

const backendUrl = getBackendUrl();

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === 'dark';
  return {
  root: {
    display: "flex",
    flexWrap: "wrap",
    width: "60%",
  },
  mainPaper: {
    width: "100%",
    marginBottom: theme.spacing(1),
    borderRadius: 8,
  },
  tabsContainer: {
    display: "inline-flex",
    backgroundColor: dark
      ? theme.palette.action.selected
      : theme.palette.background.contrast,
    borderRadius: 10,
    padding: 4,
    marginBottom: theme.spacing(2),
    width: "100%",
    boxSizing: "border-box",
  },
  tabs: {
    minHeight: 36,
    width: "100%",
    "& .MuiTabs-indicator": {
      display: "none",
    },
    "& .MuiTabs-flexContainer": {
      display: "flex",
      width: "100%",
      gap: 4,
    }
  },
  tab: {
    minHeight: 32,
    flex: 1,
    padding: theme.spacing(0.75, 2),
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    borderRadius: 8,
    color: theme.palette.text.secondary,
    transition: "background-color 0.2s ease, color 0.2s ease",
    zIndex: 1,
    "&:hover": {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
    "&.Mui-selected": {
      fontWeight: 600,
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      boxShadow: "none",
    },
    "&.Mui-selected:hover": {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
    },
    "& .MuiTab-wrapper": {
      flexDirection: "row",
      gap: theme.spacing(0.75),
    }
  },
  tabIcon: {
    marginRight: theme.spacing(0.75),
    fontSize: "1rem",
  },
  paper: {
    padding: theme.spacing(1),
    borderRadius: 8,
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  maxWidth: {
    width: "100%",
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  avatarContainer: {
    display: "flex",
    justifyContent: "center",
    position: "relative",
    marginTop: theme.spacing(0.5),
    "&:hover $avatarOverlay": {
      opacity: 1,
    },
  },
  avatar: {
    width: 65,
    height: 65,
    boxShadow: theme.shadows[3],
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  avatarOverlay: {
    position: "absolute",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    borderRadius: "50%",
    opacity: 0,
    width: 66,
    height: 66,
    margin: "auto",
  },
  editButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  roundedInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      boxShadow: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.text.disabled,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      boxShadow: "none",
    },
    "& .MuiOutlinedInput-input": {
      caretColor: theme.palette.primary.main,
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiInputLabel-shrink": {
      backgroundColor: theme.palette.background.paper,
      padding: "0 6px",
      marginLeft: -4,
      borderRadius: 4,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
    },
  },
  tabContent: {
    padding: theme.spacing(0),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: "0.75rem",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    margin: theme.spacing(0.5, 1, 1),
  },
  sectionDivider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  roundedSelect: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      boxShadow: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.text.disabled,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      boxShadow: "none",
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiInputLabel-shrink": {
      backgroundColor: theme.palette.background.paper,
      padding: "0 6px",
      marginLeft: -4,
      borderRadius: 4,
    },
  },
  profileSection: {
    backgroundColor: dark
      ? theme.palette.background.paper
      : theme.palette.background.contrast,
    borderRadius: 14,
    padding: theme.spacing(1.5, 1.5, 0.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "none",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      backgroundColor: theme.palette.background.paper,
      boxShadow: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.divider,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.text.disabled,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
      borderWidth: 2,
      boxShadow: "none",
    },
    "& .MuiInputLabel-root": {
      color: theme.palette.text.secondary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.palette.primary.main,
    },
    "& .MuiInputLabel-shrink": {
      backgroundColor: theme.palette.background.paper,
      padding: "0 6px",
      marginLeft: -4,
      borderRadius: 4,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.text.secondary,
      lineHeight: 1.4,
    },
  },
  headerContainer: {
    display: "flex",
    alignItems: "flex-start",
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  userInfoContainer: {
    marginLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flex: 1,
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  userInfoText: {
    fontSize: "0.9rem",
    color: theme.palette.text.secondary,
    "& span": {
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  },
  };
});

const UserSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
  password: Yup.string().min(5, "Too Short!").max(50, "Too Long!"),
  email: Yup.string().email("Invalid email").required("Required"),
  allHistoric: Yup.string().nullable(),
  maxConcurrentTickets: Yup.number()
    .transform((v, o) => (o === "" || o === null ? null : v))
    .nullable()
    .integer(() => i18n.t("userModal.form.maxConcurrentTicketsInvalid"))
    .min(0, () => i18n.t("userModal.form.maxConcurrentTicketsInvalid"))
    .max(1000, () => i18n.t("userModal.form.maxConcurrentTicketsInvalid")),
});

const UserModal = ({ open, onClose, userId, onImageUpdate }) => {
  const classes = useStyles();

  const toast = useToast();
  const toastRef = useRef(toast);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);
  
  const initialState = React.useMemo(() => ({
    name: "",
    signatureName: "",
    email: "",
    password: "",
    profile: "user",
    startWork: "00:00",
    endWork: "23:59",
    farewellMessage: "",
    pauseMessage: "Estou em horário de pausa. Retornarei em breve.",
    allTicket: "disabled",
    allowGroup: false,
    seeAllGroups: false,
    maxConcurrentTickets: "",
    allHistoric: "disabled",
    allUserChat: "disabled",
    userClosePendingTicket: "enabled",
    notifyOnTransferReceived: true,
    profileImage: null,
    // VoIP fields
    voipServer: "",
    voipRealm: "",
    voipExtension: "",
    voipPassword: "",
    voipEnabled: false,
  }), []);

  const { user: loggedInUser } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [user, setUser] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  // 🔗 Multi-conexões: lista de whatsappIds permitidos para criar tickets.
  // Vazio = sem permissão (admin/supervisor têm bypass server-side).
  const [selectedWhatsappIds, setSelectedWhatsappIds] = useState([]);
  const [whatsappId, setWhatsappId] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [groupSelectionLoaded, setGroupSelectionLoaded] = useState(false);
  const canManageGroupAccess =
    loggedInUser?.profile === "admin" ||
    loggedInUser?.profile === "super" ||
    loggedInUser?.super === true;
  const { loading: whatsAppsLoading, whatsApps } = useWhatsApps();
  const [tab, setTab] = useState("general");
  const [avatar, setAvatar] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [originalEmail, setOriginalEmail] = useState("");
  const [showSipPhone, setShowSipPhone] = useState(false); // ✅ Verificação do plano

  // ✅ Verificar se SIP Phone está habilitado no plano
  useEffect(() => {
    async function checkPlan() {
      if (!loggedInUser?.companyId) return;
      try {
        const planConfigs = await getPlanCompany(undefined, loggedInUser.companyId);
        if (planConfigs?.plan?.useSipPhone) {
          setShowSipPhone(true);
        } else {
          setShowSipPhone(false);
        }
      } catch (error) {
        console.warn("[UserModal] Erro ao verificar plano:", error);
        setShowSipPhone(false);
      }
    }
    checkPlan();
  }, [loggedInUser?.companyId, getPlanCompany]);

  useEffect(() => {
    // Só executar se o modal estiver aberto
    if (!open) {
      return;
    }

    if (!userId) {
      setUser(initialState);
      setOriginalEmail("");
      setAvatar(null);
      setSelectedQueueIds([]);
      setSelectedWhatsappIds([]);
      setWhatsappId("");
      setSelectedGroups([]);
      setGroupSelectionLoaded(false);
      return;
    }

    setSelectedGroups([]);
    setGroupSelectionLoaded(false);
    let isMounted = true;

    const fetchUser = async () => {
      try {
        const { data } = await api.get(`/users/${userId}`);

        if (!isMounted) return; // Evitar atualização se componente foi desmontado

        setOriginalEmail(data.email);

        setUser((prevState) => {
          const userData = { ...prevState, ...data };
          if (userData.farewellMessage === null) {
            userData.farewellMessage = "";
          }
          if (userData.signatureName === null) {
            userData.signatureName = "";
          }
          if (userData.pauseMessage === null) {
            userData.pauseMessage = "Estou em horário de pausa. Retornarei em breve.";
          }
          // A API nunca devolve a senha SIP. O campo fica vazio de propósito;
          // hasVoipPassword diz se já existe uma gravada.
          userData.voipPassword = "";
          userData.hasVoipPassword = Boolean(data.hasVoipPassword);
          if (
            userData.maxConcurrentTickets === null ||
            typeof userData.maxConcurrentTickets === "undefined"
          ) {
            userData.maxConcurrentTickets = "";
          }
          if (
            userData.notifyOnTransferReceived === null ||
            typeof userData.notifyOnTransferReceived === "undefined"
          ) {
            userData.notifyOnTransferReceived = true;
          }
          return userData;
        });

        if (data.profileImage) {
          setAvatar(`company${data.companyId}/user/${data.profileImage}`);
        }

        const userQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(userQueueIds);
        // 🔗 Carregar conexões permitidas (vem do ShowUserService include).
        // IMPORTANTE: distinguir três casos:
        //  - `data.whatsapps === undefined` → backend antigo (sem o campo);
        //    cair para fallback do `whatsappId` legado evita reconfiguração
        //    manual durante a transição.
        //  - `data.whatsapps === []` → admin esvaziou intencionalmente;
        //    NUNCA usar fallback (caso contrário, restauraríamos a permissão
        //    que ele acabou de remover ao reabrir o modal).
        //  - `data.whatsapps.length > 0` → estado canônico, usar direto.
        if (Array.isArray(data.whatsapps)) {
          setSelectedWhatsappIds(data.whatsapps.map((w) => w.id));
        } else if (data.whatsappId) {
          setSelectedWhatsappIds([data.whatsappId]);
        } else {
          setSelectedWhatsappIds([]);
        }
        setWhatsappId(data.whatsappId ? data.whatsappId : "");
      } catch (err) {
        if (isMounted) {
          toastError(err, toastRef.current);
        }
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, open, initialState]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const handleSaveUser = async (values) => {
    setIsSubmitting(true);
    try {
      const userData = {
        ...values,
        whatsappId,
        queueIds: selectedQueueIds,
        // 🔗 Multi-conexões: lista de whatsappIds que o usuário pode usar
        // para criar tickets. Vazio = sem permissão (admin/supervisor têm
        // bypass server-side).
        whatsappIds: selectedWhatsappIds,
      };

      if (userId && values.email === originalEmail) {
        delete userData.email;
      }

      if (!userData.password) {
        delete userData.password;
      }

      // Vazio = manter a senha SIP já gravada. Nunca enviar "" (o backend
      // antigo transformava isso em null e apagava o ramal).
      if (!userData.voipPassword) {
        delete userData.voipPassword;
      }
      delete userData.hasVoipPassword;

      // "" não é aceito pela API. Vazio ou lixo não numérico = sem limite (null).
      // Quem não é admin/super não envia o campo (o backend ignora de qualquer forma).
      if (!canManageGroupAccess) {
        delete userData.maxConcurrentTickets;
      } else if (userData.maxConcurrentTickets === "") {
        userData.maxConcurrentTickets = null;
      } else {
        const parsedLimit = Number(userData.maxConcurrentTickets);
        userData.maxConcurrentTickets = Number.isNaN(parsedLimit)
          ? null
          : parsedLimit;
      }

      const groupAccessApplies =
        canManageGroupAccess &&
        ["user", "supervisor"].includes(values.profile) &&
        values.allowGroup === true;
      if (groupAccessApplies) {
        userData.seeAllGroups = values.seeAllGroups === true;
        if (!userData.seeAllGroups && groupSelectionLoaded) {
          userData.groupContactIds = selectedGroups.map((group) => group.id);
        }
      } else {
        delete userData.seeAllGroups;
        delete userData.groupContactIds;
      }

      let savedUser;
      if (userId) {
        const { data } = await api.put(`/users/${userId}`, userData);
        savedUser = data;
      } else {
        const { data } = await api.post("/users", userData);
        savedUser = data;
      }

      if (avatar && typeof avatar === "object") {
        const formData = new FormData();
        formData.append("userId", savedUser.id);
        formData.append("typeArch", "user");
        formData.append("profileImage", avatar);

        await api.post(`/users/${savedUser.id}/media-upload`, formData);

        if (userId === loggedInUser.id) {
          localStorage.setItem("profileImage", savedUser.profileImage);
        }
      }

      toast.success(i18n.t("userModal.success"));

      if (userId === loggedInUser.id) {
        setTimeout(() => window.location.reload(), 1000);
      }

      onClose();
    } catch (err) {
      toastError(err, toast);
    }
    setIsSubmitting(false);
  };

  const handleDropAvatar = async (acceptedFile) => {
    try {
      const formData = new FormData();
      formData.append("userId", userId);
      formData.append("typeArch", "user");
      formData.append("profileImage", acceptedFile);

      const { data } = await api.post(
        `/users/${userId}/media-upload`,
        formData
      );

      const imageUrl = resolveUserAvatar(data.profileImage, user.companyId, backendUrl);

      setAvatar(acceptedFile);

      const socket = window.socket;
      if (socket) {
        socket.emit("user-profile-update", {
          userId,
          profileImage: data.profileImage,
          companyId: user.companyId,
        });
      }

      if (onImageUpdate) {
        onImageUpdate(imageUrl);
      }

      if (userId === loggedInUser.id) {
        localStorage.setItem("profileImage", data.profileImage);
        toast.success(i18n.t("userModal.success"));
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }

      setAvatarModalOpen(false);
      onClose();
    } catch (err) {
      toastError(err, toast);
    }
  };

  const handleDeleteAvatar = async () => {
    try {
      if (userId && user.profileImage) {
        await api.delete(`/users/${userId}/media-upload`);
        if (userId === loggedInUser.id) {
          localStorage.removeItem("profileImage");
        }
        setAvatar(null);
        setUser((prev) => ({ ...prev, profileImage: null }));
        toast.success(i18n.t("userModal.avatarModal.imageDeleted"));
      } else {
        setAvatar(null);
      }
    } catch (err) {
      toastError(err, toast);
    }
  };

  const getAvatarUrl = () => {
    if (avatar && typeof avatar === "object") {
      return URL.createObjectURL(avatar);
    } else if (user.profileImage) {
      return resolveUserAvatar(user.profileImage, user.companyId, backendUrl);
    }
    return "/nopicture.png";
  };

  const renderUserInfo = () => (
    <div className={classes.headerContainer}>
      <div className={classes.avatarContainer}>
        {" "}
        <Avatar className={classes.avatar} src={getAvatarUrl()}>
          {!avatar && !user.profileImage && <User size={24} />}
        </Avatar>
        <div className={classes.avatarOverlay}>
          <Tooltip title={i18n.t("userModal.title.editImage")} arrow>
            <IconButton
              size="small"
              className={classes.editButton}
              onClick={() => setAvatarModalOpen(true)}
            >
              <PenLine size={18} />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <div className={classes.userInfoContainer}>
        <div className={classes.userInfo}>
          <Typography className={classes.userInfoText}>
            Nome: <span>{user.name || "Sem Nome"}</span>
          </Typography>
          <Typography className={classes.userInfoText}>
            Email: <span>{user.email || "Sem Email"}</span>
          </Typography>
          <Typography className={classes.userInfoText}>
            Perfil:{" "}
            <span>
              {user.profile
                ? user.profile === "admin"
                  ? "Administrador"
                  : user.profile === "supervisor"
                  ? "Supervisor"
                  : "Usuário"
                : "Sem Perfil"}
            </span>
          </Typography>
        </div>
      </div>
    </div>
  );

  const renderTabsContent = ({ touched, errors, values, setFieldValue }) => {
    return (
      <>
        {renderUserInfo()}

        <Box className={classes.tabsContainer}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            className={classes.tabs}
            variant="fullWidth"
          >
            <Tab 
              className={classes.tab}
              label={i18n.t("userModal.tabs.general")} 
              value={"general"} 
            />
            <Tab
              className={classes.tab}
              label={i18n.t("userModal.tabs.permissions")}
              value={"permissions"}
            />
          </Tabs>
        </Box>

        <Box className={classes.tabContent}>
          <TabPanel value={tab} name={"general"}>
            <Paper elevation={0} className={classes.profileSection}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                {i18n.t("userModal.settings.personal")}
              </Typography>
              <Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.name")}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      size="small"
                      className={classes.roundedInput}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.password")}
                      type="password"
                      name="password"
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      size="small"
                      className={classes.roundedInput}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} md={8}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.email")}
                      name="email"
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      size="small"
                      className={classes.roundedInput}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Tooltip
                          title="Define o nível de acesso do usuário. Administradores têm acesso completo ao sistema."
                          arrow
                          placement="top"
                        >
                          <FormControl
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            size="small"
                            className={classes.roundedSelect}
                          >
                            <InputLabel id="profile-selection-input-label">
                              {i18n.t("userModal.form.profile")}
                            </InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t("userModal.form.profile")}
                              name="profile"
                              labelId="profile-selection-label"
                              id="profile-selection"
                              required
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="supervisor">Supervisor</MenuItem>
                              <MenuItem value="user">Usuário</MenuItem>
                            </Field>
                          </FormControl>
                        </Tooltip>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.signatureName")}
                      name="signatureName"
                      helperText={i18n.t("userModal.form.signatureNameHelper")}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      size="small"
                      inputProps={{ maxLength: 100 }}
                      className={classes.roundedInput}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            <Paper elevation={0} className={classes.profileSection}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                {i18n.t("userModal.settings.general")}
              </Typography>
              <Box>
                <Grid container spacing={1}>
                  <Grid item xs={12}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editQueues"
                      yes={() => (
                        <Tooltip
                          title="Define as filas/departamentos que o usuário pode atender. Os tickets destas filas aparecerão para o atendente."
                          arrow
                          placement="top"
                        >
                          <div>
                            <QueueSelect
                              selectedQueueIds={selectedQueueIds}
                              onChange={(values) => setSelectedQueueIds(values)}
                              fullWidth
                            />
                          </div>
                        </Tooltip>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <FormControl
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          size="small"
                          disabled={whatsAppsLoading}
                          className={classes.roundedSelect}
                        >
                          <InputLabel>
                            {i18n.t("userModal.form.whatsapp")}
                          </InputLabel>
                          <Select
                            value={whatsappId}
                            onChange={(e) => setWhatsappId(e.target.value)}
                            label={i18n.t("userModal.form.whatsapp")}
                          >
                            <MenuItem value={""}>&nbsp;</MenuItem>
                            {whatsApps.map((whatsapp) => (
                              <MenuItem key={whatsapp.id} value={whatsapp.id}>
                                {whatsapp.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Tooltip
                          title="Conexões que este usuário pode usar para criar novos tickets / enviar mensagens iniciais. Vazio = só pode usar a conexão padrão da empresa (modo legado). Para restringir realmente, adicione pelo menos UMA conexão que NÃO seja a padrão. Admin e supervisor têm acesso a todas, independente desta lista."
                          arrow
                          placement="top"
                        >
                          <div>
                            <WhatsappSelect
                              selectedWhatsappIds={selectedWhatsappIds}
                              onChange={(values) => setSelectedWhatsappIds(values)}
                              disabled={whatsAppsLoading}
                            />
                          </div>
                        </Tooltip>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.startWork")}
                          type="time"
                          ampm={"false"}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            step: 600,
                          }}
                          name="startWork"
                          error={touched.startWork && Boolean(errors.startWork)}
                          helperText={touched.startWork && errors.startWork}
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          size="small"
                          className={classes.roundedInput}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Can
                      role={loggedInUser.profile}
                      perform="user-modal:editProfile"
                      yes={() => (
                        <Field
                          as={TextField}
                          label={i18n.t("userModal.form.endWork")}
                          type="time"
                          ampm={"false"}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          inputProps={{
                            step: 600,
                          }}
                          name="endWork"
                          error={touched.endWork && Boolean(errors.endWork)}
                          helperText={touched.endWork && errors.endWork}
                          variant="outlined"
                          margin="normal"
                          fullWidth
                          size="small"
                          className={classes.roundedInput}
                        />
                      )}
                    />
                  </Grid>

                  {/* 👋 Mensagem de Despedida - Todos os usuários podem configurar */}
                  <Grid item xs={12} md={12}>
                    <Field
                      as={TextField}
                      label={i18n.t("userModal.form.farewellMessage")}
                      type="text"
                      multiline
                      rows={2}
                      fullWidth
                      name="farewellMessage"
                      error={
                        touched.farewellMessage &&
                        Boolean(errors.farewellMessage)
                      }
                      helperText={
                        (touched.farewellMessage && errors.farewellMessage) ||
                        "Esta mensagem personalizada será enviada ao finalizar atendimentos (sobrescreve a mensagem padrão da conexão)"
                      }
                      variant="outlined"
                      margin="normal"
                      size="small"
                      className={classes.roundedInput}
                    />
                  </Grid>

                  {/* ⏸️ Mensagem de Pausa - Todos os usuários podem configurar */}
                  <Grid item xs={12} md={12}>
                    <Field
                      as={TextField}
                      label="Mensagem de Pausa (Horário de Almoço/Intervalo)"
                      type="text"
                      multiline
                      rows={2}
                      fullWidth
                      name="pauseMessage"
                      error={
                        touched.pauseMessage &&
                        Boolean(errors.pauseMessage)
                      }
                      helperText={
                        (touched.pauseMessage && errors.pauseMessage) ||
                        "Esta mensagem será enviada se alguém tentar transferir um ticket para você enquanto estiver em pausa"
                      }
                      variant="outlined"
                      margin="normal"
                      size="small"
                      className={classes.roundedInput}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Paper>

            {/* 📞 VoIP / Softphone - Configurações de ramal SIP */}
            {/* ✅ Só aparece se SIP Phone estiver habilitado no plano */}
            {showSipPhone && (
              <Can
                role={loggedInUser.profile}
                perform="user-modal:editProfile"
                yes={() => (
                  <Paper elevation={0} className={classes.profileSection}>
                    <Typography variant="subtitle2" className={classes.sectionTitle}>
                      📞 VoIP / Softphone
                    </Typography>
                    <Box>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label="Servidor WebSocket"
                            name="voipServer"
                            placeholder="wss://fusion.empresa.com.br:7443"
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                            helperText="URL completa do WebSocket (wss://...)"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Field
                            as={TextField}
                            label="Domínio SIP (Realm)"
                            name="voipRealm"
                            placeholder="webrtc.empresa.com.br"
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                            helperText="Domínio para autenticação SIP"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            label="Ramal"
                            name="voipExtension"
                            placeholder="Ex: 1001"
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                            helperText="Número do ramal"
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Field
                            as={TextField}
                            label="Senha SIP"
                            name="voipPassword"
                            type="password"
                            placeholder={
                              user.hasVoipPassword
                                ? "Deixe em branco para manter"
                                : "Senha do ramal"
                            }
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            size="small"
                            className={classes.roundedInput}
                            helperText={
                              user.hasVoipPassword
                                ? "Senha já cadastrada. Preencha só para trocar."
                                : "Obrigatória para o softphone conectar"
                            }
                            InputLabelProps={{ shrink: true }}
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <FormControl
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            size="small"
                            className={classes.roundedSelect}
                          >
                            <InputLabel>Habilitar VoIP</InputLabel>
                            <Field
                              as={Select}
                              label="Habilitar VoIP"
                              name="voipEnabled"
                            >
                              <MenuItem value={true}>Habilitado</MenuItem>
                              <MenuItem value={false}>Desabilitado</MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                )}
              />
            )}
          </TabPanel>

          <TabPanel value={tab} name={"permissions"}>
            <Can
              role={loggedInUser.profile}
              perform="user-modal:editProfile"
              yes={() => (
                <>
                  <Paper elevation={0} className={classes.profileSection}>
                    <Box>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <Tooltip
                            title="Quando ativado, permite visualizar tickets sem fila atribuída. Caso contrário, o usuário só verá tickets de suas filas."
                            arrow
                            placement="top"
                          >
                            <FormControl
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              size="small"
                              className={classes.roundedSelect}
                            >
                              <InputLabel>
                                {i18n.t("userModal.form.allTicket")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("userModal.form.allTicket")}
                                name="allTicket"
                                required
                              >
                                <MenuItem value="enabled">
                                  {i18n.t("userModal.form.allTicketEnable")}
                                </MenuItem>
                                <MenuItem value="disabled">
                                  {i18n.t("userModal.form.allTicketDisable")}
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Tooltip
                            title="Quando ativado, permite acessar e responder a conversas em grupo. Se desativado, mensagens de grupo não serão exibidas."
                            arrow
                            placement="top"
                          >
                            <FormControl
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              size="small"
                              className={classes.roundedSelect}
                            >
                              <InputLabel>
                                {i18n.t("userModal.form.allowGroup")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("userModal.form.allowGroup")}
                                name="allowGroup"
                                required
                              >
                                <MenuItem value={true}>
                                  {i18n.t("userModal.form.allTicketEnable")}
                                </MenuItem>
                                <MenuItem value={false}>
                                  {i18n.t("userModal.form.allTicketDisable")}
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Tooltip>
                        </Grid>
                        {canManageGroupAccess && (
                          <Grid item xs={12} md={6}>
                            <Field
                              as={TextField}
                              name="maxConcurrentTickets"
                              type="number"
                              label={i18n.t("userModal.form.maxConcurrentTickets")}
                              error={
                                touched.maxConcurrentTickets &&
                                Boolean(errors.maxConcurrentTickets)
                              }
                              helperText={
                                (touched.maxConcurrentTickets &&
                                  errors.maxConcurrentTickets) ||
                                i18n.t("userModal.form.maxConcurrentTicketsHelp")
                              }
                              variant="outlined"
                              margin="normal"
                              size="small"
                              fullWidth
                              inputProps={{ min: 0, max: 1000, step: 1 }}
                              className={classes.roundedInput}
                            />
                          </Grid>
                        )}
                        {canManageGroupAccess &&
                          ["user", "supervisor"].includes(values?.profile) &&
                          values?.allowGroup === true && (
                            <Grid item xs={12}>
                              <GroupAccessField
                                targetUserId={userId}
                                whatsappId={whatsappId}
                                seeAllGroups={values.seeAllGroups === true}
                                onSeeAllGroupsChange={(checked) => setFieldValue("seeAllGroups", checked)}
                                selected={selectedGroups}
                                onSelectedChange={setSelectedGroups}
                                onSelectionLoaded={(groups) => {
                                  setSelectedGroups(groups);
                                  setGroupSelectionLoaded(true);
                                }}
                              />
                            </Grid>
                          )}
                        <Grid item xs={12} md={6}>
                          <Tooltip
                            title="Quando ativado, permite ver todo o histórico de tickets, incluindo tickets de outros atendentes e de todas as filas, independente das suas filas atribuídas."
                            arrow
                            placement="top"
                          >
                            <FormControl
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              size="small"
                              className={classes.roundedSelect}
                            >
                              <InputLabel>
                                {i18n.t("userModal.form.allHistoric")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("userModal.form.allHistoric")}
                                name="allHistoric"
                                required
                              >
                                <MenuItem value="disabled">
                                  {i18n.t("userModal.form.allHistoricDisabled")}
                                </MenuItem>
                                <MenuItem value="enabled">
                                  {i18n.t("userModal.form.allHistoricEnabled")}
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Tooltip
                            title="Quando ativado, permite ver e responder a conversas de outros atendentes, mas apenas nas filas a que o usuário tem acesso."
                            arrow
                            placement="top"
                          >
                            <FormControl
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              size="small"
                              className={classes.roundedSelect}
                            >
                              <InputLabel>
                                {i18n.t("userModal.form.allUserChat")}
                              </InputLabel>
                              <Field
                                as={Select}
                                label={i18n.t("userModal.form.allUserChat")}
                                name="allUserChat"
                                required
                              >
                                <MenuItem value="disabled">
                                  {i18n.t("userModal.form.allHistoricDisabled")}
                                </MenuItem>
                                <MenuItem value="enabled">
                                  {i18n.t("userModal.form.allHistoricEnabled")}
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Tooltip>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl
                            variant="outlined"
                            margin="normal"
                            fullWidth
                            size="small"
                            className={classes.roundedSelect}
                          >
                            <InputLabel>
                              {i18n.t("userModal.form.userClosePendingTicket")}
                            </InputLabel>
                            <Field
                              as={Select}
                              label={i18n.t(
                                "userModal.form.userClosePendingTicket"
                              )}
                              name="userClosePendingTicket"
                              required
                            >
                              <MenuItem value="disabled">
                                {i18n.t("userModal.form.allHistoricDisabled")}
                              </MenuItem>
                              <MenuItem value="enabled">
                                {i18n.t("userModal.form.allHistoricEnabled")}
                              </MenuItem>
                            </Field>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Tooltip
                            title="Quando ativado, o atendente recebe som e notificação dedicados ao ser destinatário de uma transferência de ticket."
                            arrow
                            placement="top"
                          >
                            <FormControl
                              variant="outlined"
                              margin="normal"
                              fullWidth
                              size="small"
                              className={classes.roundedSelect}
                            >
                              <InputLabel>
                                Notificar transferência recebida
                              </InputLabel>
                              <Field
                                as={Select}
                                label="Notificar transferência recebida"
                                name="notifyOnTransferReceived"
                                required
                              >
                                <MenuItem value={true}>
                                  Ativado
                                </MenuItem>
                                <MenuItem value={false}>
                                  Desativado
                                </MenuItem>
                              </Field>
                            </FormControl>
                          </Tooltip>
                        </Grid>
                      </Grid>
                    </Box>
                  </Paper>
                </>
              )}
            />
          </TabPanel>
        </Box>
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Formik
        key={userId || "new"}
        initialValues={user}
        enableReinitialize={true}
        validationSchema={UserSchema}
        onSubmit={handleSaveUser}
      >
        {({ touched, errors, isValid, values, setFieldValue }) => (
          <>
            <AvatarModal
              open={avatarModalOpen}
              onClose={() => setAvatarModalOpen(false)}
              onSave={handleDropAvatar}
              currentAvatar={getAvatarUrl()}
              onDelete={handleDeleteAvatar}
            />

            <StandardModal
              open={open}
              onClose={onClose}
              onConfirm={() => {
                if (isValid) {
                  document
                    .getElementById("userModalForm")
                    .dispatchEvent(
                      new Event("submit", { cancelable: true, bubbles: true })
                    );
                }
              }}
              title={
                userId
                  ? `${i18n.t("userModal.title.edit")}`
                  : `${i18n.t("userModal.title.add")}`
              }
              maxWidth="sm"
              primaryButton={{
                text: userId
                  ? `${i18n.t("userModal.buttons.okEdit")}`
                  : `${i18n.t("userModal.buttons.okAdd")}`,
                color: "primary",
                disabled: isSubmitting,
                loading: isSubmitting,
              }}
              secondaryButton={{
                text: i18n.t("userModal.buttons.cancel"),
                color: "secondary",
                disabled: isSubmitting,
              }}
            >
              <Form id="userModalForm">
                {renderTabsContent({ touched, errors, values, setFieldValue })}
              </Form>
            </StandardModal>
          </>
        )}
      </Formik>
    </div>
  );
};

export default UserModal;
